import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, SlidersHorizontal, ChevronDown, Check } from 'lucide-react';

const sortOptions = ['Recommended', 'Price: Low to high', 'Price: High to low', 'Top rated'];
const sizeOptions = ['Any size', 'Compact', 'Sedan', 'SUV', 'Van'];
const companyOptions = ['All companies', 'GAMBGO Rentals', 'Prime Auto', 'Coastal Cars', 'Urban Wheels'];

type Panel = 'sort' | 'size' | 'company' | 'all' | null;

const RideRentalCarsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [panel, setPanel] = useState<Panel>(null);
  const [sortBy, setSortBy] = useState(sortOptions[0]);
  const [carSize, setCarSize] = useState(sizeOptions[0]);
  const [company, setCompany] = useState(companyOptions[0]);

  const state = (location.state as { pickupLocation?: string; startDateISO?: string; endDateISO?: string } | null) ?? undefined;

  const pickupLabel = state?.pickupLocation || 'Greater Banjul Area';

  const dateLabel = useMemo(() => {
    if (!state?.startDateISO || !state?.endDateISO) return 'Select dates in Rentals';

    const format = (iso: string) =>
      new Intl.DateTimeFormat('en-GB', {
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Africa/Banjul',
      }).format(new Date(iso));

    return `${format(state.startDateISO)} - ${format(state.endDateISO)}`;
  }, [state?.startDateISO, state?.endDateISO]);

  const activeFilters = [sortBy !== sortOptions[0] ? sortBy : null, carSize !== sizeOptions[0] ? carSize : null, company !== companyOptions[0] ? company : null].filter(Boolean) as string[];

  const resetAll = () => {
    setSortBy(sortOptions[0]);
    setCarSize(sizeOptions[0]);
    setCompany(companyOptions[0]);
  };

  const renderOptionList = (title: string, options: string[], selected: string, onPick: (value: string) => void) => (
    <div className="bg-white rounded-t-2xl p-5 max-h-[70vh] overflow-y-auto">
      <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-4" />
      <h3 className="text-lg font-bold text-gray-900 mb-4">{title}</h3>
      <div className="space-y-2">
        {options.map((option) => (
          <button
            key={option}
            onClick={() => {
              onPick(option);
              setPanel(null);
            }}
            className="w-full flex items-center justify-between rounded-xl border border-gray-200 px-4 py-3"
          >
            <span className="text-sm font-medium text-gray-800">{option}</span>
            {selected === option ? <Check className="w-4 h-4 text-teal-600" /> : null}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white safe-area-top">
      <div className="flex items-center justify-between px-4 pt-12 pb-4 border-b border-gray-100">
        <button onClick={() => navigate('/ride/rentals')} className="h-10 w-10 flex items-center justify-center">
          <ChevronLeft className="h-5 w-5 text-gray-800" />
        </button>
        <h1 className="text-base font-semibold text-gray-900">Choose your car</h1>
        <button onClick={resetAll} className="text-sm font-medium text-teal-600">Reset</button>
      </div>

      <div className="mx-4 mt-4 mb-4 p-4 border border-gray-200 rounded-xl">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900">{pickupLabel}</p>
            <p className="text-xs text-gray-500 mt-0.5">{dateLabel}</p>
          </div>
          <button onClick={() => navigate('/ride/rentals')} className="text-sm font-medium text-teal-600">Edit</button>
        </div>
      </div>

      <div className="flex gap-2 px-4 mb-3 overflow-x-auto pb-1">
        <button onClick={() => setPanel('all')} className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full border border-gray-200 bg-white">
          <SlidersHorizontal className="w-4 h-4 text-gray-600" />
        </button>
        <button onClick={() => setPanel('sort')} className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full border border-gray-200 bg-white">
          <span className="text-sm font-medium text-gray-700">{sortBy}</span>
          <ChevronDown className="w-3 h-3 text-gray-500" />
        </button>
        <button onClick={() => setPanel('size')} className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full border border-gray-200 bg-white">
          <span className="text-sm font-medium text-gray-700">{carSize}</span>
          <ChevronDown className="w-3 h-3 text-gray-500" />
        </button>
        <button onClick={() => setPanel('company')} className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full border border-gray-200 bg-white">
          <span className="text-sm font-medium text-gray-700">{company}</span>
          <ChevronDown className="w-3 h-3 text-gray-500" />
        </button>
      </div>

      {activeFilters.length > 0 && (
        <div className="px-4 mb-5 flex gap-2 flex-wrap">
          {activeFilters.map((item) => (
            <span key={item} className="text-xs font-medium px-3 py-1 rounded-full bg-gray-100 text-gray-700">{item}</span>
          ))}
        </div>
      )}

      <div className="flex flex-col items-center justify-center px-6 py-16">
        <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <span className="text-3xl">🚗</span>
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">No cars available yet</h3>
        <p className="text-sm text-gray-500 text-center max-w-xs">
          Filters are active and ready. Cars will appear here as soon as inventory is added.
        </p>
      </div>

      {panel && (
        <div className="fixed inset-0 z-30 bg-black/35 flex items-end" onClick={() => setPanel(null)}>
          <div className="w-full" onClick={(e) => e.stopPropagation()}>
            {panel === 'sort' && renderOptionList('Sort by', sortOptions, sortBy, setSortBy)}
            {panel === 'size' && renderOptionList('Car size', sizeOptions, carSize, setCarSize)}
            {panel === 'company' && renderOptionList('Rental company', companyOptions, company, setCompany)}
            {panel === 'all' && (
              <div className="bg-white rounded-t-2xl p-5">
                <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-4" />
                <h3 className="text-lg font-bold text-gray-900 mb-4">All filters</h3>
                <div className="grid grid-cols-1 gap-2">
                  <button onClick={() => setPanel('sort')} className="text-left rounded-xl border border-gray-200 px-4 py-3 text-sm font-medium text-gray-800">Sort by</button>
                  <button onClick={() => setPanel('size')} className="text-left rounded-xl border border-gray-200 px-4 py-3 text-sm font-medium text-gray-800">Car size</button>
                  <button onClick={() => setPanel('company')} className="text-left rounded-xl border border-gray-200 px-4 py-3 text-sm font-medium text-gray-800">Rental company</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RideRentalCarsPage;
